"use client";
import { X } from "lucide-react";
import LangButton from "@/components/ui/LangButton";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import {
  DocumentInput,
  ProductClientState,
  ProductClientStateSchema,
  ProductCreateInputSchema,
  ProductCreateInput,
  ProductPropertyAssignment,
  ThreeDFormState,
} from "@/zod/product.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import Image from "next/image";
import { useLocale } from "next-intl";
import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ZodError } from "zod";
import { v4 as uuidv4 } from "uuid";
import { ProductFormSections } from "./EditProductFormSection";
import EditDocsPanel from "./EditDocsPanel";
import EditThreeDPanel from "./EditThreeDPanel";
import ProductFormSkeleton from "./ProductFormSkeleton";
import { ProductImageUploader } from "./ProductImageUploader";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  PreviewSVG,
  FarbinPlaceholderSVG,
  CrossSVG,
  TikSVG,
  TrashCanSVG,
} from "@/components/ui/SVG";
import { PERMISSIONS } from "@/core/permissions";
import EditProductPropertiesSection from "./EditProductPropertiesSection";
import { useSession } from "next-auth/react";
import { createSlugFromPartNumber, normalizeSlugInput } from "@/utils/slug";
import { AnimatePresence, motion } from "framer-motion";
import { ConfirmOverlay } from "@/components/ui/ConfirmOverlay";
import { MediaPickerOverlay } from "@/media/components/MediaPickerOverlay";
import { buildAssetUrl } from "@/media/utils";
import { Asset } from "@/media/types";
import { useActiveEditorLock } from "@/hooks/useActiveEditorLock";
import { getLocalizedUserName } from "@/utils/userLocalization";

const NEW_PRODUCT_TEMPLATE = {
  hero_image: "",
  heroAssetId: null as string | null,
  part_number: "",
  slug: "",
  description_title_en: "",
  description_title_fa: "",
  description_text_en: "",
  description_text_fa: "",
  about_en: "",
  about_fa: "",
  category_image: "",
  categoryAssetId: null as string | null,
  threeD: [] as ThreeDFormState[],
};

type ProductUsageSource = {
  id?: string | null;
  heroAssetId?: string | null;
  categoryAssetId?: string | null;
  highlights?: Array<{ id?: string | null; assetId?: string | null }>;
  documents?: Array<{ id?: string | null; assetId?: string | null }>;
  threeD?: Array<{ id?: string | null; assetId?: string | null }>;
};

type UsageEntry = {
  productId?: string | null;
  contentId?: string | null;
  fieldPath?: string | null;
};

const toNullableId = (value: unknown): string | null => {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return String(value);
  return null;
};

const normalizeUsageEntry = (usage: any): UsageEntry | null => {
  const productId = toNullableId(usage?.productId);
  const contentId = toNullableId(usage?.contentId);
  const fieldPath =
    typeof usage?.fieldPath === "string"
      ? usage.fieldPath.trim() || null
      : null;
  if (!productId && !contentId && !fieldPath) return null;
  return { productId, contentId, fieldPath };
};

const dedupeUsages = (usages: UsageEntry[]) => {
  const map = new Map<string, UsageEntry>();
  usages.forEach((usage) => {
    const key = `${usage.productId ?? ""}|${usage.contentId ?? ""}|${usage.fieldPath ?? ""}`;
    if (!map.has(key)) map.set(key, usage);
  });
  return Array.from(map.values());
};

const buildProductUsageMap = (
  product: ProductUsageSource | null | undefined,
): Map<string, UsageEntry[]> => {
  const map = new Map<string, UsageEntry[]>();
  const productId = toNullableId(product?.id);
  if (!product || !productId) return map;

  const addUsage = (assetId: string | null, fieldPath?: string | null) => {
    if (!assetId || !fieldPath) return;
    const list = map.get(assetId) ?? [];
    list.push({ productId, fieldPath });
    map.set(assetId, list);
  };

  addUsage(toNullableId(product.heroAssetId), "product.hero");
  addUsage(toNullableId(product.categoryAssetId), "product.category");

  (product.highlights ?? []).forEach((h) => {
    const highlightId = toNullableId(h?.id);
    const assetId = toNullableId(h?.assetId);
    if (highlightId) addUsage(assetId, `product.highlight.${highlightId}`);
  });

  (product.documents ?? []).forEach((d) => {
    const docId = toNullableId(d?.id);
    const assetId = toNullableId(d?.assetId);
    if (docId) addUsage(assetId, `product.document.${docId}`);
  });

  (product.threeD ?? []).forEach((m) => {
    const modelId = toNullableId(m?.id);
    const assetId = toNullableId(m?.assetId);
    if (modelId) addUsage(assetId, `product.3d.${modelId}`);
  });

  return map;
};

const transformProductForApi = (
  values: ProductClientState,
): ProductCreateInput => {
  const toNullableFloat = (value: unknown): number | null => {
    if (value === "" || value === null || value === undefined) return null;
    const num =
      typeof value === "string"
        ? parseFloat(value)
        : typeof value === "number"
          ? value
          : Number(value);
    if (!Number.isFinite(num)) return null;
    return num;
  };

  const productPropertyAssignments = (values.productProperty ?? [])
    .map((entry: any): ProductPropertyAssignment | null => {
      if (!entry) return null;
      const propertyId = entry.propertyId || entry.property?.id;
      const optionId = entry.optionId || entry.option?.id;
      const groupId = entry.groupId || entry.group?.id || null;
      if (!propertyId || !optionId) return null;
      return {
        propertyId,
        optionId,
        groupId,
      };
    })
    .filter((entry): entry is ProductPropertyAssignment => entry !== null);

  const payload = {
    ...values,
    heroAssetId: values.heroAssetId?.trim() ? values.heroAssetId.trim() : null,
    highlights: values.highlights?.map((h) => ({
      image_url: h.image_url,
      assetId: h.assetId?.trim() ? h.assetId.trim() : null,
      title_en: h.title_en,
      title_fa: h.title_fa,
      info: h.info,
    })),
    documents: (values.documents || [])
      .filter((d: any) => d && typeof d === "object" && d.url && d.url !== "")
      .map((d: any) => {
        const sizeNum =
          typeof d.file_size === "string"
            ? parseInt(d.file_size, 10) || 0
            : d.file_size || 0;
        let dateStr = d.file_date || "";
        if (dateStr && typeof dateStr === "string" && !dateStr.includes("T")) {
          dateStr = new Date(dateStr).toISOString();
        }
        const typeStr = (d.file_type || "").toString().trim() || "pdf";
        return {
          url: d.url,
          assetId: d.assetId?.trim() ? d.assetId.trim() : null,
          file_date: dateStr,
          file_type: typeStr,
          file_size: Math.max(1, sizeNum),
          file_lang: d.file_lang && d.file_lang !== "" ? d.file_lang : "EN",
          file_title:
            d.file_title && d.file_title !== "" ? d.file_title : d.file_slug,
          file_slug: d.file_slug,
        } as DocumentInput;
      }),
    threeD: (values.threeD || [])
      .filter((model: any) => model && typeof model === "object" && model.url)
      .map((model: any) => ({
        url: model.url,
        assetId: model.assetId?.trim() ? model.assetId.trim() : null,
        title_en: model.title_en?.trim() ? model.title_en.trim() : null,
        title_fa: model.title_fa?.trim() ? model.title_fa.trim() : null,
        exposure: toNullableFloat(model.exposure) ?? undefined,
        shadow_intensity: toNullableFloat(model.shadow_intensity) ?? undefined,
      })),
    categoryAssetId: values.categoryAssetId?.trim()
      ? values.categoryAssetId.trim()
      : null,
    productProperty: productPropertyAssignments,
  } as unknown as ProductCreateInput;

  const normalizedSlug = payload.slug?.trim()
    ? normalizeSlugInput(payload.slug)
    : createSlugFromPartNumber(payload.part_number);

  return ProductCreateInputSchema.parse({
    ...payload,
    slug: normalizedSlug,
  });
};

const saveProductFn = async (product: ProductCreateInput) => {
  const isUpdateMode = !!product.id;
  const url = isUpdateMode ? `/api/products/${product.id}` : "/api/products";
  const method = isUpdateMode ? "PUT" : "POST";

  const payload = ProductCreateInputSchema.parse(product);

  const response = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to save product.");
  }
  return response.json();
};

const deleteProductFn = async (productId: string) => {
  const response = await fetch(`/api/products/${productId}`, {
    method: "DELETE",
  });

  let responseBody: any = null;
  try {
    responseBody = await response.json();
  } catch {
    // ignore body parsing errors
  }

  if (!response.ok) {
    throw new Error(
      responseBody?.message || "Failed to delete product. Please try again.",
    );
  }

  return responseBody;
};

const fetchProductsFn = async () => {
  const response = await fetch("/api/products");
  const data = await response.json();
  return data.products;
};

function ProductEditor({
  onClose,
  defaultProducer,
  initialSlug,
  focusPartNumber = true,
}: {
  onClose?: () => void;
  defaultProducer?: string;
  initialSlug?: string;
  focusPartNumber?: boolean;
}) {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  const locale =
    typeof document !== "undefined" && document.documentElement.lang === "fa"
      ? "fa"
      : "en";
  const userName =
    (session?.user
      ? getLocalizedUserName(locale, session.user).fullName
      : "") ||
    session?.user?.email ||
    "Unknown";
  const userEmail = session?.user?.email ?? null;
  const userPermissions = (session?.user as any)?.permissions ?? [];
  const canDeleteProduct = userPermissions.includes(PERMISSIONS.PRODUCT.DELETE);
  const canCreateProduct = userPermissions.includes(PERMISSIONS.PRODUCT.CREATE);
  const canEditProduct = userPermissions.includes(PERMISSIONS.PRODUCT.EDIT);
  const loadRequestRef = useRef<AbortController | null>(null);
  useEffect(() => () => loadRequestRef.current?.abort(), []);

  const productForm = useForm<ProductClientState>({
    resolver: zodResolver(ProductClientStateSchema),
    defaultValues: {
      ...NEW_PRODUCT_TEMPLATE,
      producer: defaultProducer ?? "",
      productProperty: [],
    },
    mode: "onChange",
  });

  const { getValues, setValue, reset, watch, setFocus, register } = productForm;

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    desc: true,
  });
  const [currentLang, setCurrentLang] = useState<"en" | "fa">("en");
  const routeLocale = useLocale();
  const partNumber = watch("part_number");
  const producer = watch("producer");
  const productId = watch("id");
  const slugValue = watch("slug");
  const categoryImage = watch("category_image");
  const [suggestionList, setSuggestionList] = useState<ProductClientState[]>(
    [],
  );
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [isProductLoading, setIsProductLoading] = useState(false);
  const [isHeroImageLoading] = useState(false);
  const [isCategoryImageLoading] = useState(false);
  const [isCategoryImageDeleting, setIsCategoryImageDeleting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState<boolean>(false);
  const [isDeleteOverlayOpen, setIsDeleteOverlayOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductClientState | null>(null);
  const [uiState, dispatch] = useReducer(uiReducer, {
    docsOpen: false,
    galleryOpen: false,
    modelOpen: false,
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [heroImageError, setHeroImageError] = useState<string | null>(null);
  const [heroImageSuccess, setHeroImageSuccess] = useState<string | null>(null);
  const [, setCategoryImageError] = useState<string | null>(null);
  const [, setCategoryImageSuccess] = useState<string | null>(null);
  const [isHeroPickerOpen, setIsHeroPickerOpen] = useState(false);
  const [isCategoryPickerOpen, setIsCategoryPickerOpen] = useState(false);
  const [highlightPickerFor, setHighlightPickerFor] = useState<string | null>(
    null,
  );
  const [docPickerFor, setDocPickerFor] = useState<{
    slug: string;
    title: string;
  } | null>(null);
  const [threeDPickerFor, setThreeDPickerFor] = useState<string | null>(null);
  const [showUpdatedState, setShowUpdatedState] = useState(false);
  const updateSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const saveProductMutation = useMutation({
    mutationFn: saveProductFn,
    onSuccess: (data) => {
      setFormError(null);
      setFormSuccess(data?.message || "Saved successfully.");
      if (!isUpdateMode) {
        handleResetForm();
      }
    },
    onError: (error: any) => {
      const fallbackMessage = "Failed to save product. Please try again.";
      setFormSuccess(null);
      setFormError(
        error?.message && error.message.length < 160
          ? error.message
          : fallbackMessage,
      );
    },
  });
  const isSaving = saveProductMutation.isPending;

  const deleteProductMutation = useMutation({
    mutationFn: deleteProductFn,
    onSuccess: (data) => {
      setFormError(null);
      setFormSuccess(data?.message || "Product deleted successfully.");
      setIsDeleteOverlayOpen(false);
      handleResetForm();
      setSelectedProduct(null);
      setIsUpdateMode(false);
      setShowSuggestion(false);
      refetchProducts();
    },
    onError: (error: any) => {
      const fallbackMessage = "Failed to delete product. Please try again.";
      setFormSuccess(null);
      setFormError(
        error?.message && error.message.length < 160
          ? error.message
          : fallbackMessage,
      );
      setIsDeleteOverlayOpen(false);
    },
  });
  const isDeleting = deleteProductMutation.isPending;

  const { data: products, refetch: refetchProducts } = useQuery<
    ProductClientState[]
  >({
    queryKey: ["products"],
    queryFn: fetchProductsFn,
    staleTime: 0,
  });

  useEffect(() => {
    if (products) {
      setSuggestionList(
        products.map((p) => ({
          ...p,
          part_number: p.part_number.toLowerCase(),
        })),
      );
    }
  }, [products]);

  useEffect(() => {
    if (!partNumber) {
      setSuggestionList([]);
      return;
    }
    if (products) {
      const filtered = products.filter(
        (s) =>
          s.part_number.toLowerCase().includes(partNumber.toLowerCase()) &&
          s.producer === producer,
      );
      setSuggestionList(filtered);
    }
  }, [partNumber, products, producer]);

  useEffect(() => {
    if (
      isUpdateMode &&
      selectedProduct &&
      partNumber !== selectedProduct.part_number
    ) {
      setIsUpdateMode(false);
      setSelectedProduct(null);
      setValue("hero_image", "");
      setValue("heroAssetId", null);
      setValue("documents", []);
      setValue("threeD", []);
      setValue("id", undefined);
      setValue("categoryAssetId", null);
      setValue("category_image", "");
      const highlights = getValues("highlights");
      const resetHighlights = highlights?.map((h) => ({
        ...h,
        image_url: "",
        assetId: null,
      }));
      setValue("highlights", resetHighlights);
      setSlugManuallyEdited(false);
    }
  }, [
    getValues,
    isUpdateMode,
    partNumber,
    products,
    selectedProduct,
    setValue,
  ]);

  useEffect(() => {
    if (productId) return;
    if (!partNumber) {
      setValue("slug", "", { shouldValidate: true });
      setSlugManuallyEdited(false);
      return;
    }
    if (slugManuallyEdited) return;
    setValue("slug", createSlugFromPartNumber(partNumber), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [partNumber, productId, slugManuallyEdited, setValue]);

  useEffect(() => {
    return () => {
      if (updateSuccessTimeoutRef.current) {
        clearTimeout(updateSuccessTimeoutRef.current);
      }
    };
  }, []);

  const shouldLock = Boolean(productId && userId);
  const {
    status: lockStatus,
    lockedBy: productLockedBy,
    retry: retryLock,
  } = useActiveEditorLock({
    activePath: productId ? `product/${productId}` : null,
    userId,
    userName,
    userEmail,
    enabled: shouldLock,
  });
  const lockBlocked =
    shouldLock && lockStatus !== "active" && lockStatus !== "idle";

  const onSectionToggle = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleResetForm = useCallback(() => {
    reset({
      ...NEW_PRODUCT_TEMPLATE,
      producer: defaultProducer ?? "",
    });
    setSlugManuallyEdited(false);
    setHeroImageSuccess(null);
    setCategoryImageSuccess(null);
    setHeroImageError(null);
    setCategoryImageError(null);
    setHighlightPickerFor(null);
    setDocPickerFor(null);
    setThreeDPickerFor(null);
  }, [defaultProducer, reset]);

  const openDocumentPicker = (slug: string, title: string) => {
    setDocPickerFor({ slug, title });
  };

  const handleDocumentAssetSelect = (asset: Asset) => {
    if (!docPickerFor) return;
    const url = buildAssetUrl(asset);
    const assetExtension = (asset.extension || "")
      .toString()
      .replace(/^\./, "")
      .toLowerCase();
    const mimeExtension = (asset.mimeType || "").split("/").pop() || "";
    const prevDocuments = getValues("documents") || [];
    const docIndex = prevDocuments.findIndex(
      (d) => d.file_slug === docPickerFor.slug,
    );
    const existing = docIndex > -1 ? prevDocuments[docIndex] : null;

    const nextDoc: DocumentInput = {
      file_slug: docPickerFor.slug,
      file_title:
        existing?.file_title || docPickerFor.title || docPickerFor.slug,
      url,
      assetId: asset.id,
      file_size: Math.max(
        1,
        Math.round(((asset.size || 0) as number) / 1024) || 1,
      ),
      file_type: assetExtension || mimeExtension || "file",
      file_date: existing?.file_date || new Date().toISOString(),
      file_lang: existing?.file_lang || "EN",
    };

    const newDocuments = [...prevDocuments];
    if (docIndex > -1) {
      newDocuments[docIndex] = { ...existing, ...nextDoc };
    } else {
      newDocuments.push(nextDoc);
    }

    setValue("documents", newDocuments, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setDocPickerFor(null);
  };

  const openThreeDPicker = (modelUuid?: string) => {
    setThreeDPickerFor(modelUuid ?? "new");
  };

  const handleThreeDAssetSelect = (asset: Asset) => {
    if (!threeDPickerFor) return;
    const url = buildAssetUrl(asset);
    const currentModels = (getValues("threeD") || []) as ThreeDFormState[];

    if (threeDPickerFor === "new") {
      const newModel: ThreeDFormState = {
        uuid: uuidv4(),
        id: undefined,
        url,
        assetId: asset.id,
        title_en: "",
        title_fa: "",
        exposure: "",
        shadow_intensity: "",
      };
      setValue("threeD", [...currentModels, newModel], {
        shouldValidate: true,
        shouldDirty: true,
      });
      setThreeDPickerFor(null);
      return;
    }

    const updatedModels = currentModels.map((model) =>
      model.uuid === threeDPickerFor
        ? { ...model, url, assetId: asset.id }
        : model,
    );
    setValue("threeD", updatedModels, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setThreeDPickerFor(null);
  };

  const handleThreeDModelDelete = async (modelUuid: string) => {
    const currentModels = (getValues("threeD") || []) as ThreeDFormState[];
    const targetModel = currentModels.find((model) => model.uuid === modelUuid);
    if (!targetModel) return;

    const updatedModels = currentModels.filter(
      (model) => model.uuid !== modelUuid,
    );
    setValue("threeD", updatedModels, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleHeroAssetSelect = (asset: Asset) => {
    const url = buildAssetUrl(asset);
    setValue("hero_image", url, { shouldValidate: true, shouldDirty: true });
    setValue("heroAssetId", asset.id, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setHeroImageError(null);
    setHeroImageSuccess("Selected from media library.");
  };

  const handleCategoryAssetSelect = (asset: Asset) => {
    const url = buildAssetUrl(asset);
    setValue("category_image", url, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("categoryAssetId", asset.id, {
      shouldDirty: true,
      shouldValidate: false,
    });
    setCategoryImageError(null);
    setCategoryImageSuccess("Selected from media library.");
  };

  const openHighlightPicker = (highlightUuid: string) => {
    setHighlightPickerFor(highlightUuid);
  };

  const handleHighlightAssetSelect = (asset: Asset) => {
    if (!highlightPickerFor) return;
    const url = buildAssetUrl(asset);
    const prevHighlights = getValues("highlights") || [];
    const updatedHighlights = prevHighlights.map((h) =>
      h.uuid === highlightPickerFor
        ? { ...h, image_url: url, assetId: asset.id }
        : h,
    );
    setValue("highlights", updatedHighlights, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setHighlightPickerFor(null);
  };

  const handleCategoryImageDelete = async () => {
    const currentUrl = getValues("category_image");
    if (!currentUrl) return;
    const id = getValues("id");
    if (!id) {
      setValue("category_image", "", { shouldValidate: true });
      setValue("categoryAssetId", null, { shouldDirty: true });
      setCategoryImageError(null);
      setCategoryImageSuccess("Category image cleared.");
      return;
    }
    try {
      setIsCategoryImageDeleting(true);
      const res = await fetch(`/api/products/${id}/category-image`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "Failed to delete category image.");
      }
      setValue("category_image", "", { shouldValidate: true });
      setValue("categoryAssetId", null, { shouldDirty: true });
      setCategoryImageError(null);
      setCategoryImageSuccess("Category image unassigned.");
    } catch (error: any) {
      const message =
        error?.message && error.message.length < 160
          ? error.message
          : "Failed to delete category image.";
      setCategoryImageSuccess(null);
      setCategoryImageError(message);
    } finally {
      setIsCategoryImageDeleting(false);
    }
  };

  const handleDeleteRequest = () => {
    if (!isUpdateMode || !canDeleteProduct) return;
    const id = getValues("id");
    if (!id) return;
    setFormError(null);
    setFormSuccess(null);
    setIsDeleteOverlayOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const id = getValues("id");
    if (!id || isDeleting) return;
    setFormError(null);
    setFormSuccess(null);
    try {
      await deleteProductMutation.mutateAsync(id);
    } catch (error) {
      console.error("Failed to delete product.", error);
    }
  };

  const handleSelectProduct = useCallback(
    async (productSlug: string) => {
      loadRequestRef.current?.abort();
      const request = new AbortController();
      loadRequestRef.current = request;
      setIsProductLoading(true);
      setHighlightPickerFor(null);
      setDocPickerFor(null);
      setThreeDPickerFor(null);
      try {
        const res = await fetch(
          `/api/products/by-slug/${encodeURIComponent(productSlug)}`,
          { signal: request.signal },
        );
        if (!res.ok) throw new Error("Product not found.");

        const { product: fetchedProduct } = await res.json();

        const clientReadyProduct: ProductClientState = {
          ...fetchedProduct,
          hero_image:
            fetchedProduct.heroAsset?.url || fetchedProduct.hero_image || "",
          heroAssetId: fetchedProduct.heroAsset?.id ?? null,
          category_image:
            fetchedProduct.categoryAsset?.url ||
            fetchedProduct.category_image ||
            "",
          categoryAssetId: fetchedProduct.categoryAsset?.id ?? null,
          category_ids: fetchedProduct.categoryProducts.map(
            (c: any) => c.category.id,
          ),
          documents: (fetchedProduct.documents ?? []).map((d: any) => {
            const { asset, ...rest } = d;
            return {
              ...rest,
              url: asset?.url || d.url || "",
              assetId: asset?.id ?? null,
            };
          }),
          highlights: fetchedProduct.highlights.map((h: any) => {
            const { asset, ...rest } = h;
            return {
              ...rest,
              image_url: asset?.url || h.image_url || "",
              assetId: asset?.id ?? null,
              uuid: uuidv4(),
            };
          }),
          threeD: (fetchedProduct.threeD ?? []).map((model: any) => ({
            ...(() => {
              const { asset, ...rest } = model;
              return {
                ...rest,
                url: asset?.url || model.url || "",
                assetId: asset?.id ?? null,
              };
            })(),
            uuid: uuidv4(),
            title_en: model.title_en ?? "",
            title_fa: model.title_fa ?? "",
            exposure: model.exposure ?? "",
            shadow_intensity: model.shadow_intensity ?? "",
          })),
        };

        const assignmentsRes = await fetch(
          `/api/products/${fetchedProduct.id}/properties`,
          { signal: request.signal },
        );
        if (!assignmentsRes.ok)
          throw new Error("Could not load product properties.");
        const assignments = await assignmentsRes.json();
        if (request.signal.aborted) return;
        clientReadyProduct.productProperty = assignments
          .map((a: any) => ({
            propertyId: a.propertyId,
            optionId: a.optionId,
            groupId: a.groupId,
          }))
          .filter((a: any) => !!a.groupId);
        reset(clientReadyProduct);
        setHeroImageSuccess(null);
        setCategoryImageSuccess(null);
        setHeroImageError(null);
        setCategoryImageError(null);
        setIsUpdateMode(true);
        setSelectedProduct(clientReadyProduct);
      } catch (error) {
        if (request.signal.aborted) return;
        console.error(error);
        setFormSuccess(null);
        setFormError("Could not load product data.");
        handleResetForm();
      } finally {
        if (!request.signal.aborted) setIsProductLoading(false);
      }
    },
    [
      handleResetForm,
      reset,
      setDocPickerFor,
      setHighlightPickerFor,
      setIsProductLoading,
      setIsUpdateMode,
      setSelectedProduct,
      setThreeDPickerFor,
      setCategoryImageError,
      setCategoryImageSuccess,
      setFormError,
      setFormSuccess,
      setHeroImageError,
      setHeroImageSuccess,
    ],
  );

  useEffect(() => {
    if (initialSlug) void handleSelectProduct(initialSlug);
  }, [initialSlug, handleSelectProduct]);

  const syncProductAssetUsages = useCallback(
    async (
      product: ProductUsageSource | null | undefined,
      prevProduct?: ProductUsageSource | null,
    ) => {
      const usageMap = buildProductUsageMap(product);
      const productId = toNullableId(product?.id);
      if (!productId) return true;

      const previousUsageMap = buildProductUsageMap(prevProduct);
      const currentAssetIds = new Set(usageMap.keys());
      const prevAssetIds = new Set(previousUsageMap.keys());
      const removedAssetIds = [...prevAssetIds].filter(
        (assetId) => !currentAssetIds.has(assetId),
      );

      const fetchExistingUsages = async (assetId: string) => {
        try {
          const res = await fetch(`/api/media/assets/${assetId}/usages`, {
            cache: "no-store",
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || data?.error);
          const usages = Array.isArray(data?.usages) ? data.usages : [];
          return usages
            .map(normalizeUsageEntry)
            .filter((u: UsageEntry): u is UsageEntry => Boolean(u));
        } catch (error) {
          console.error(`Failed to load usages for asset ${assetId}`, error);
          return [];
        }
      };

      const postUsages = async (assetId: string, usages: UsageEntry[]) => {
        const res = await fetch(`/api/media/assets/${assetId}/usages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ usages }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            data?.message || data?.error || "Failed to update usages.",
          );
        }
      };

      const updateTasks = Array.from(usageMap.entries()).map(
        async ([assetId, productUsages]) => {
          const existing = await fetchExistingUsages(assetId);
          const merged = dedupeUsages([
            ...existing.filter(
              (usage: UsageEntry) => usage.productId !== productId,
            ),
            ...productUsages,
          ]);
          await postUsages(assetId, merged);
        },
      );

      const clearTasks = removedAssetIds.map(async (assetId) => {
        const existing = await fetchExistingUsages(assetId);
        const remaining = dedupeUsages(
          existing.filter((usage: UsageEntry) => usage.productId !== productId),
        );
        await postUsages(assetId, remaining);
      });

      const results = await Promise.allSettled([...updateTasks, ...clearTasks]);
      return results.every((result) => result.status === "fulfilled");
    },
    [],
  );

  const handleSubmit = async (formValues: ProductClientState) => {
    setSubmitAttempted(true);
    setFormSuccess(null);
    setFormError(null);
    if (updateSuccessTimeoutRef.current) {
      clearTimeout(updateSuccessTimeoutRef.current);
      updateSuccessTimeoutRef.current = null;
    }
    setShowUpdatedState(false);
    if (!isUpdateMode && !canCreateProduct) {
      setFormError("You do not have permission to create products.");
      return;
    }
    try {
      let payload: ProductCreateInput;
      try {
        payload = transformProductForApi(formValues);
      } catch (e: any) {
        if (e instanceof ZodError) {
          e.issues.forEach((issue) => {
            const path = issue.path.join(".");
            productForm.setError(path as any, {
              type: "schema",
              message: issue.message,
            });
            const sectionKey = getSectionKeyFromPath(path);
            if (sectionKey) {
              setOpenSections((prev) => ({ ...prev, [sectionKey]: true }));
            }
            if (path.startsWith("documents")) {
              if (!uiState.docsOpen) dispatch({ type: "TOGGLE_DOCS" });
            }
          });
          const firstIssue = e.issues[0];
          const issuePath = firstIssue?.path.join(".");
          setFormError(
            firstIssue
              ? `${issuePath ? `${issuePath}: ` : ""}${firstIssue.message}`
              : "Please fix the highlighted fields.",
          );
          return;
        }
        throw e;
      }
      const previousProduct = selectedProduct;
      const response = await saveProductMutation.mutateAsync(payload);
      if (isUpdateMode) {
        setShowUpdatedState(true);
        updateSuccessTimeoutRef.current = setTimeout(() => {
          setShowUpdatedState(false);
          updateSuccessTimeoutRef.current = null;
        }, 2000);
      }
      const savedProduct = (response as any)?.product;
      const usageSyncOk = await syncProductAssetUsages(
        savedProduct,
        previousProduct,
      );
      if (!usageSyncOk) {
        setFormError(
          "Product saved, but media usage records could not be fully updated.",
        );
      }
      await refetchProducts();
    } catch (err: any) {
      const message =
        err?.message && err.message.length < 160
          ? err.message
          : "Failed to save product. Please review highlighted fields.";
      setFormError(message);
    }
  };

  const previewSlug =
    slugValue || getValues("slug") || getValues("part_number") || "";

  const handlePreviewClick = () => {
    if (!previewSlug) return;
    const href = `/${routeLocale}/product/${encodeURIComponent(previewSlug)}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  function getSectionKeyFromPath(path: string): string | null {
    if (
      path.startsWith("description_title_") ||
      path.startsWith("description_text_")
    )
      return "desc";
    if (path.startsWith("about_")) return "about";
    if (path.startsWith("key_features")) return "key_features";
    if (path.startsWith("mech_chars")) return "mech_char";
    if (path.startsWith("phys_chars")) return "phys_char";
    if (path.startsWith("fire_standards")) return "fire_stans";
    if (path.startsWith("highlights")) return "highlights";
    if (path.startsWith("documents")) return "docs";
    return null;
  }

  useEffect(() => {
    if (focusPartNumber) {
      setFocus("part_number");
    }
  }, [setFocus, focusPartNumber]);

  if (session && !canEditProduct) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 py-8 text-center text-sm text-gray-600">
        <p className="text-slateCustom text-lg font-semibold">
          You do not have permission to edit products.
        </p>
        {onClose ? (
          <button
            className="mt-4 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            onClick={onClose}
          >
            Close
          </button>
        ) : null}
      </div>
    );
  }

  if (lockBlocked) {
    const title =
      lockStatus === "waiting"
        ? "Another editor is active"
        : lockStatus === "lost"
          ? "You lost the editing lock"
          : "Checking lock…";
    const lockedByLabel =
      productLockedBy?.userName || productLockedBy?.userEmail || "Someone else";
    const lockedByDetail =
      productLockedBy?.userEmail && productLockedBy?.userName
        ? `${productLockedBy.userName} (${productLockedBy.userEmail})`
        : productLockedBy?.userEmail || productLockedBy?.userName || null;
    const description =
      lockStatus === "waiting"
        ? `${lockedByDetail ?? lockedByLabel} is editing this product.`
        : lockStatus === "lost"
          ? "Please try to re-acquire the lock."
          : "Ensuring exclusive access before editing.";

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-6 py-8 text-center">
        {lockStatus === "loading" ? (
          <DotPulseLdr color="var(--color-slateCustom)" />
        ) : null}
        <div className="text-slateCustom space-y-2">
          <p className="text-xl font-semibold">{title}</p>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <div className="flex items-center gap-3">
          {onClose ? (
            <button
              type="button"
              aria-label="Close"
              className="bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-9 w-9 items-center justify-center rounded-full transition"
              onClick={onClose}
            >
              <X className="h-4 w-4" strokeWidth={4} />
            </button>
          ) : null}
          {lockStatus !== "loading" ? (
            <button
              className="bg-pinkCustom rounded-md px-4 py-2 text-sm font-semibold text-white"
              onClick={() => retryLock()}
            >
              Retry
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-grayCustom min-h-full lg:bg-white">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 pb-24">
        <FormProvider {...productForm}>
          <form
            onSubmit={productForm.handleSubmit(handleSubmit, (errors) => {
              setSubmitAttempted(true);
              const firstKey = Object.keys(errors)[0];
              if (firstKey) {
                productForm.setFocus(firstKey as any);
                const sectionKey = getSectionKeyFromPath(firstKey);
                if (sectionKey) {
                  setOpenSections((prev) => ({ ...prev, [sectionKey]: true }));
                }
              }
              setFormError("Please fix the highlighted fields.");
            })}
          >
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto">
                  <button
                    className="border-slateCustom text-slateCustom lg:bg-slateCustom w-16 rounded-md border py-2 text-sm font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 lg:w-24 lg:text-white"
                    type="submit"
                    disabled={
                      isSaving ||
                      isDeleting ||
                      (!isUpdateMode && !canCreateProduct)
                    }
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      {isSaving ? (
                        <motion.span
                          key="saving"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.18 }}
                          className="inline-flex items-center"
                        >
                          Saving...
                        </motion.span>
                      ) : showUpdatedState && isUpdateMode ? (
                        <motion.span
                          key="updated"
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.18 }}
                          className="inline-flex items-center gap-1"
                        >
                          Updated
                          <TikSVG className="h-2.75 w-2.75" svgColor="white" />
                        </motion.span>
                      ) : (
                        <motion.span
                          key={isUpdateMode ? "update" : "create"}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -4 }}
                          transition={{ duration: 0.18 }}
                          className="inline-flex items-center"
                        >
                          {isUpdateMode ? "Update" : "Create"}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </button>

                  <button
                    type="button"
                    onClick={() => (onClose ? onClose() : handleResetForm())}
                    aria-label="Close"
                    className="bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-9 w-9 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 lg:hidden"
                    disabled={isSaving || isDeleting}
                  >
                    <X className="h-4 w-4" strokeWidth={4} />
                  </button>
                  <div className="ml-auto lg:hidden">
                    <LangButton
                      currentLang={currentLang}
                      setCurrentLang={setCurrentLang}
                      bgColor="grayCustom"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handlePreviewClick}
                    className="text-slateCustom hidden items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 lg:flex"
                    disabled={!previewSlug}
                  >
                    <PreviewSVG className="h-5 w-5" svgColor="slateCustom" />
                    Preview
                  </button>
                </div>

                <div
                  dir="ltr"
                  className="ml-auto flex w-full min-w-0 flex-1 items-start gap-3 lg:min-w-[260px]"
                >
                  <div className="relative min-w-0 flex-1">
                    <input
                      type="text"
                      id="partNumberDesktop"
                      autoComplete="off"
                      placeholder="Part Number"
                      className={`w-full rounded-md border border-gray-300 bg-white p-2 uppercase ${
                        productForm.formState.errors.part_number
                          ? "border-red-500"
                          : ""
                      }`}
                      {...register("part_number", {
                        onChange: (e) => {
                          const val = e.target.value.toUpperCase();
                          e.target.value = val;
                          setShowSuggestion(val.length > 1);
                        },
                      })}
                    />
                    {partNumber && partNumber.length > 0 && (
                      <button
                        type="button"
                        aria-label="Clear part number"
                        className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => {
                          setValue("part_number", "");
                          setShowSuggestion(false);
                          setFocus("part_number");
                        }}
                      >
                        <CrossSVG className="w-3" svgColor="slateCustom" />
                      </button>
                    )}
                    {productForm.formState.errors.part_number && (
                      <p className="mt-1 text-xs text-red-500">
                        {productForm.formState.errors.part_number.message}
                      </p>
                    )}
                    <AnimatePresence>
                      {showSuggestion && (
                        <motion.ul
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full z-40 mt-1 max-h-60 w-full overflow-y-auto rounded bg-white pl-6 shadow"
                        >
                          {suggestionList
                            ?.filter((p) =>
                              p.part_number
                                .toUpperCase()
                                .includes(partNumber.toUpperCase()),
                            )
                            .map((sugg) => (
                              <li
                                key={sugg.part_number}
                                className="flex cursor-pointer items-center space-x-2 border-b border-b-gray-200 py-1 text-sm"
                                onClick={() => {
                                  handleSelectProduct(sugg.slug);
                                  setShowSuggestion(false);
                                }}
                              >
                                <Image
                                  src={sugg.hero_image}
                                  alt="suggestion"
                                  width={100}
                                  height={100}
                                  className="bg-grayCustom w-16 rounded-sm"
                                />
                                <span>{sugg.part_number}</span>
                              </li>
                            ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                  <button
                    type="button"
                    onClick={() => (onClose ? onClose() : handleResetForm())}
                    className="text-slateCustom hover:text-pinkCustom hidden min-h-10 items-center rounded-md px-3 text-sm font-semibold disabled:opacity-60 lg:inline-flex"
                    disabled={isSaving || isDeleting}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>

            {(formError || formSuccess) && (
              <div
                role={formError ? "alert" : "status"}
                aria-live="polite"
                className={`flex items-center justify-between gap-3 border px-4 py-2 text-sm lg:hidden ${formError ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`}
              >
                <span>{formError || formSuccess}</span>
                <button
                  type="button"
                  aria-label="Dismiss message"
                  className="flex h-9 w-9 shrink-0 items-center justify-center"
                  onClick={() => {
                    setFormError(null);
                    setFormSuccess(null);
                  }}
                >
                  <CrossSVG className="h-3 w-3" svgColor="slateCustom" />
                </button>
              </div>
            )}
            {isProductLoading ? (
              <ProductFormSkeleton />
            ) : (
              <>
                <div className="rounded-md bg-white px-4 py-3">
                  <ProductImageUploader
                    onPick={() => setIsHeroPickerOpen(true)}
                    mode="pick"
                    isLoading={isHeroImageLoading}
                    id="heroImageDesktop"
                    helperError={heroImageError || undefined}
                    helperSuccess={heroImageSuccess || undefined}
                  />
                </div>

                <div className="text-slateCustom flex justify-between border-y border-y-gray-200 bg-white text-center text-[1.1rem] font-medium">
                  <div
                    className="w-1/3 cursor-pointer py-2"
                    onClick={() => dispatch({ type: "TOGGLE_DOCS" })}
                  >
                    Documents
                  </div>
                  <div className="w-1/3 cursor-pointer border-x border-x-gray-200 py-2">
                    Gallery
                  </div>
                  <button
                    type="button"
                    className="w-1/3 py-2"
                    onClick={() => dispatch({ type: "TOGGLE_MODEL" })}
                  >
                    3D Model
                  </button>
                </div>
                <EditDocsPanel
                  onDocumentPick={openDocumentPicker}
                  isOpen={uiState.docsOpen}
                />
                <EditThreeDPanel
                  isOpen={uiState.modelOpen}
                  onModelPick={openThreeDPicker}
                  onModelDelete={handleThreeDModelDelete}
                />

                <div className="bg-white py-6">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div
                      className={
                        currentLang === "en"
                          ? "space-y-2"
                          : "hidden space-y-2 lg:block"
                      }
                    >
                      <h3 className="text-slateCustom hidden text-lg font-semibold lg:block">
                        English
                      </h3>
                      <div className="border-t border-gray-200 bg-white py-3">
                        <ProductFormSections
                          currentLang="en"
                          openSections={openSections}
                          onSectionToggle={onSectionToggle}
                          onHighlightAssetPick={openHighlightPicker}
                          submitAttempted={submitAttempted}
                        />
                      </div>
                    </div>
                    <div
                      className={
                        currentLang === "fa"
                          ? "space-y-2"
                          : "hidden space-y-2 lg:block"
                      }
                      dir="rtl"
                    >
                      <h3 className="text-slateCustom hidden text-right text-lg font-semibold lg:block">
                        فارسی
                      </h3>
                      <div className="border-t border-gray-200 bg-white py-3">
                        <ProductFormSections
                          currentLang="fa"
                          openSections={openSections}
                          onSectionToggle={onSectionToggle}
                          onHighlightAssetPick={openHighlightPicker}
                          submitAttempted={submitAttempted}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className="mt-6 bg-white px-0 py-1"
                    dir={currentLang === "fa" ? "rtl" : "ltr"}
                  >
                    <div className="overflow-hidden rounded-md border border-gray-200 bg-white px-4">
                      <div className="flex items-center justify-between">
                        <h3 className="py-2 text-xl font-bold">
                          {currentLang === "fa"
                            ? "عکس دسته‌بندی"
                            : "Category Image"}
                        </h3>
                        <span className="text-xs text-gray-400">
                          1:1 recommended
                        </span>
                      </div>
                      <div className="pb-3">
                        <div className="flex items-center justify-between rounded-md bg-white p-2">
                          <button
                            type="button"
                            onClick={() => setIsCategoryPickerOpen(true)}
                            className="relative flex aspect-video w-34 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed border-gray-400"
                            aria-busy={isCategoryImageLoading}
                            disabled={isCategoryImageLoading}
                          >
                            {categoryImage ? (
                              <Image
                                src={categoryImage || ""}
                                alt="Category Preview"
                                width={96}
                                height={54}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <FarbinPlaceholderSVG className="h-2/3 w-2/3" />
                            )}
                            {isCategoryImageLoading && (
                              <div className="bg-opacity-60 absolute inset-0 z-10 flex items-center justify-center bg-white">
                                <div className="border-slateCustom h-10 w-10 animate-spin rounded-full border-b-4"></div>
                              </div>
                            )}
                          </button>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleCategoryImageDelete}
                              className="flex items-center gap-2 rounded-md bg-gray-200 px-3 py-2 disabled:opacity-50"
                              disabled={
                                isCategoryImageLoading ||
                                isCategoryImageDeleting ||
                                !categoryImage
                              }
                            >
                              {isCategoryImageDeleting ? (
                                <DotPulseLdr size={20} color="#334155" />
                              ) : (
                                <TrashCanSVG className="h-4 w-4" />
                              )}
                              <span>
                                {isCategoryImageDeleting
                                  ? "Removing..."
                                  : "Remove"}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <EditProductPropertiesSection
                    productId={getValues("id")}
                    currentLang={currentLang}
                  />

                  <div className="px-1">
                    {formError && (
                      <div
                        className="my-2 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700"
                        role="alert"
                      >
                        {formError}
                      </div>
                    )}
                    {formSuccess && (
                      <div
                        className="my-2 rounded border border-green-300 bg-green-50 p-2 text-sm text-green-700"
                        role="status"
                        aria-live="polite"
                      >
                        {formSuccess}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-8">
              <button
                type="button"
                onClick={handlePreviewClick}
                disabled={!previewSlug}
                className="text-slateCustom flex min-h-11 items-center gap-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold disabled:opacity-60 lg:hidden"
              >
                <PreviewSVG className="h-5 w-5" svgColor="slateCustom" />
                Preview
              </button>
              {isUpdateMode && canDeleteProduct ? (
                <button
                  type="button"
                  onClick={handleDeleteRequest}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={isSaving || isDeleting}
                >
                  Delete
                </button>
              ) : null}
            </div>
          </form>
        </FormProvider>
      </div>

      <AnimatePresence>
        {isDeleteOverlayOpen && canDeleteProduct && (
          <ConfirmOverlay
            title={currentLang === "fa" ? "حذف محصول" : "Delete product"}
            message={
              currentLang === "fa"
                ? "این عملیات محصول و تمام داده‌های مرتبط را به طور دائمی حذف می‌کند."
                : "This will permanently remove the product and all related data."
            }
            onCancel={() => setIsDeleteOverlayOpen(false)}
            onConfirm={handleDeleteConfirm}
            confirmLabel="Delete"
            variant="danger"
            disableActions={isDeleting}
          />
        )}
      </AnimatePresence>

      <MediaPickerOverlay
        open={isHeroPickerOpen}
        onClose={() => setIsHeroPickerOpen(false)}
        onSelect={(asset) => handleHeroAssetSelect(asset)}
        accept={["image/*"]}
        usage={{
          context: `product-hero-${getValues("part_number")}`,
          productId: getValues("id") || undefined,
          contentId: getValues("part_number") || undefined,
        }}
        title={currentLang === "fa" ? "انتخاب تصویر اصلی" : "Select hero image"}
      />
      <MediaPickerOverlay
        open={isCategoryPickerOpen}
        onClose={() => setIsCategoryPickerOpen(false)}
        onSelect={(asset) => handleCategoryAssetSelect(asset)}
        accept={["image/*"]}
        usage={{
          context: `product-category-${getValues("part_number")}`,
          productId: getValues("id") || undefined,
          contentId: getValues("part_number") || undefined,
        }}
        title={
          currentLang === "fa"
            ? "انتخاب تصویر دسته‌بندی"
            : "Select category image"
        }
      />
      <MediaPickerOverlay
        open={!!highlightPickerFor}
        onClose={() => setHighlightPickerFor(null)}
        onSelect={(asset) => handleHighlightAssetSelect(asset)}
        accept={["image/*"]}
        usage={{
          context: `product-highlight-${getValues("part_number")}`,
          productId: getValues("id") || undefined,
          contentId: getValues("part_number") || undefined,
        }}
        title={
          currentLang === "fa" ? "انتخاب تصویر ویژگی" : "Select highlight image"
        }
      />
      <MediaPickerOverlay
        open={!!docPickerFor}
        onClose={() => setDocPickerFor(null)}
        onSelect={(asset) => handleDocumentAssetSelect(asset)}
        accept={["document", "application/*", "text/*", "image/*"]}
        usage={{
          context: `product-document-${getValues("part_number")}`,
          productId: getValues("id") || undefined,
          contentId: getValues("part_number") || undefined,
        }}
        title={
          currentLang === "fa" ? "انتخاب فایل سند" : "Select document file"
        }
      />
      <MediaPickerOverlay
        open={!!threeDPickerFor}
        onClose={() => setThreeDPickerFor(null)}
        onSelect={(asset) => handleThreeDAssetSelect(asset)}
        accept={["model/*", ".glb", ".gltf"]}
        usage={{
          context: `product-3d-${getValues("part_number")}`,
          productId: getValues("id") || undefined,
          contentId: getValues("part_number") || undefined,
        }}
        title={currentLang === "fa" ? "انتخاب مدل سه‌بعدی" : "Select 3D model"}
      />
    </div>
  );
}

export default ProductEditor;

type UIState = {
  docsOpen: boolean;
  galleryOpen: boolean;
  modelOpen: boolean;
};

type UIAction =
  | { type: "TOGGLE_DOCS" }
  | { type: "TOGGLE_GALLERY" }
  | { type: "TOGGLE_MODEL" };

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case "TOGGLE_DOCS":
      return { ...state, docsOpen: !state.docsOpen };
    case "TOGGLE_GALLERY":
      return { ...state, galleryOpen: !state.galleryOpen };
    case "TOGGLE_MODEL":
      return { ...state, modelOpen: !state.modelOpen };
    default:
      return state;
  }
}
